import { colors } from "@constants/theme";

import React, { useCallback, useMemo, useState } from "react";
import {
  FlatList,
  ActivityIndicator,
  StyleSheet,
  View,
  TouchableOpacity,
  Modal,
} from "react-native";
import {
  getCrateDeliveryDetails,
  getDailyCratesItems,
  getDailyRTOSV3,
} from "@services/api";
import useSarvCall from "@hooks/useSarvCall";
import EmptySearchList from "@components/SearchHeader/EmptySearchList";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import t from "@constants/localization";
import { useFocusEffect } from "@react-navigation/native";
import CustomText from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import { getDayMonth } from "@utils/getFormattedDate";
import MyOrderSwitch from "./components/MyOrderSwitch";
import CrateListCard from "./components/CrateListCard";
import SortingTabButton from "./components/SortingTabButton";
import BottomButton from "./components/BottomButton";
import CxCard from "../components/CxCard";
import InfoTooltipModal from "../components/InfoTooltipModal";
import HeaderWithSearch from "../HeaderWithSearch";
import ProgressBar from "./components/ProgressBar";
import SarvCallModal from "../../MyTasks/components/SarvCallModal.tsx";

function SortingDeliveries({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isCrateCurrentTab, setIsCrateCurrentTab] = useState(true);
  const [cratesData, setCratesData] = useState([]);
  const [filterCratesData, setFilterCratesData] = useState([]);
  const [crateProgress, setCrateProgress] = useState({});
  const { forNotDelivered = false, date = "" } = route?.params || {};
  const [ordersData, setOrdersData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [infoTooltipVisible, setInfoTooltipVisible] = useState(false);
  const [infoTooltipData, setInfoTooltipData] = useState({});
  const [isDisputed, setIsDisputed] = useState(false);
  const [disputedtext, setDisputedText] = useState("");
  const [rescheduleddata, setRescheduledData] = useState({});
  const { createCall, showSarvModal, setShowSarvModal, updateSarvData } =
    useSarvCall();
  const toggleSwitch = () => setIsEnabled((previousState) => !previousState);

  const PATH = "screens/MyDeliveries/SortingDeliveries/index";

  useFocusEffect(
    useCallback(() => {
      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCrateCurrentTab, isEnabled]),
  );

  const fetchData = () => {
    setIsLoading(true);
    if (isCrateCurrentTab) {
      fetchCrateData();
    } else {
      fetchCustomerOrdersData();
    }
  };

  const isTodayDate = (dateParam) => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    const todayDate = `${yyyy}-${mm}-${dd}`;
    return dateParam === todayDate;
  };

  const fetchCrateData = async () => {
    try {
      const { data } = await getDailyCratesItems(
        isEnabled ? { type: "MY_ORDERS", date } : { date },
      );
      setCratesData(data?.data);
      setFilterCratesData(data?.data);
      setCrateProgress(data?.progress);
      setIsDisputed(data?.is_any_item_disputed);
      setDisputedText(data?.disputed_return_text);
      const rescheduledItem = data?.data.find(
        (item) => item.type === "RESCHEDULED",
      );
      if (rescheduledItem) {
        setRescheduledData(rescheduledItem.next_payload);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  const onRefresh = () => {
    setIsRefreshing(true);
    if (isCrateCurrentTab) {
      fetchCrateData();
    } else {
      fetchCustomerOrdersData();
    }
    setIsRefreshing(false);
  };

  const fetchCustomerOrdersData = async () => {
    try {
      if (forNotDelivered) {
        const { data } = await getDailyRTOSV3({
          date: route?.params?.date,
          spokeName: route?.params?.spokeName,
        });
        setOrdersData(data);
      } else {
        const { data } = await getCrateDeliveryDetails({
          date: route?.params?.date,
          spokeName: route?.params?.spokeName,
          type: "ASSIGNED",
        });
        setOrdersData(data);
        setUsersData(data?.usersTodaysDeliveries);
        updateSarvData(data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const keyExtractor = (item, index) =>
    isCrateCurrentTab ? item.crate_id : `${item.user_id}-${index}`;
  const cp_db_id = useSelector(({ auth }) => auth?.user?.cp_db_id);
  const is_cmo_cp = useSelector(({ auth }) => auth?.user?.is_cmo_cp);

  const onPressCrossIcon = () => onSearchInputChange("");

  const onSearchInputChange = debounce((text) => {
    if (isCrateCurrentTab) {
      if (!text) {
        setCratesData(filterCratesData);
        return;
      }
      const filteredData = filterCratesData.filter((v) =>
        containText(text, v?.id),
      );
      setCratesData(filteredData);
    } else {
      const filteredData = {
        ...ordersData,
        usersTodaysDeliveries: usersData.filter(
          (v) =>
            containText(text, v?.user_name) ||
            containText(text, v?.user_phone) ||
            containText(text, v?.cx_formatted_address),
        ),
      };
      setOrdersData(filteredData);
    }
  }, 500);

  const containText = (text, item) => {
    const lowerCasedText = text?.trim().toLowerCase();
    return item?.toLowerCase().includes(lowerCasedText);
  };

  const renderItem = ({ item }) => {
    if (isCrateCurrentTab) {
      return (
        <CrateListCard
          crate={item}
          navigation={navigation}
          route={route}
          type={isEnabled ? "MY_ORDERS" : ""}
        />
      );
    } else {
      return (
        <CxCard
          item={item}
          date={ordersData?.date}
          spokeName={route?.params?.spokeName}
          setInfoTooltipData={setInfoTooltipData}
          setInfoTooltipVisible={setInfoTooltipVisible}
          type={route?.params?.type}
          createCall={createCall}
        />
      );
    }
  };

  const sortUpdates = useMemo(
    () =>
      function () {
        return ordersData.usersTodaysDeliveries;
      },
    [ordersData],
  );

  const checkListIsEmpty = () => {
    if (isLoading) {
      return false;
    }
    if (isCrateCurrentTab) {
      return !(cratesData && cratesData.length > 0);
    }
    return !(ordersData && ordersData?.usersTodaysDeliveries?.length > 0);
  };

  const seeAllSamasya = () => {
    navigation.navigate("AllSamasya", {
      spokeName: route?.params?.spokeName,
      date: route?.params?.date,
    });
  };
  return (
    <View style={styles.container}>
      <HeaderWithSearch
        headertitle={
          isTodayDate(route?.params?.date)
            ? t(`${PATH}/headerTitle`)
            : t(`${PATH}/deliveryDetails`, {
                date: getDayMonth(route?.params?.date),
              })
        }
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
        searchPlaceholder={
          isCrateCurrentTab
            ? t(`${PATH}/crateSearchPlaceholder`)
            : t(`${PATH}/customerSearchPlaceholder`)
        }
        isSearchEnabled={true}
        onPressCrossIcon={onPressCrossIcon}
        onSearchInputChange={onSearchInputChange}
        rightComponent={
          cp_db_id && (
            <MyOrderSwitch toggleSwitch={toggleSwitch} isEnabled={isEnabled} />
          )
        }
        bottomComponent={
          ordersData ? (
            isCrateCurrentTab ? (
              <ProgressBar
                title={crateProgress.title}
                completed={crateProgress.completed}
                total={crateProgress.total}
              />
            ) : (
              <ProgressBar
                title={t(`${PATH}/customerProgressText`)}
                completed={ordersData.submittedUsers}
                total={ordersData.totalUsers}
              />
            )
          ) : null
        }
      />
      <View style={styles.tabContainer}>
        <SortingTabButton
          title={t(`${PATH}/crateAndItem`)}
          isActive={isCrateCurrentTab}
          onPress={() => setIsCrateCurrentTab(true)}
        />
        <SortingTabButton
          title={t(`${PATH}/customer`)}
          isActive={!isCrateCurrentTab}
          onPress={() => setIsCrateCurrentTab(false)}
        />
      </View>
      <SarvCallModal
        showSarvModal={showSarvModal}
        setShowSarvModal={setShowSarvModal}
      />
      <InfoTooltipModal
        visible={infoTooltipVisible}
        setVisible={setInfoTooltipVisible}
        data={infoTooltipData}
      />
      {checkListIsEmpty() ? (
        <EmptySearchList />
      ) : (
        <>
          <FlatList
            keyExtractor={keyExtractor}
            contentContainerStyle={[
              styles.containerStyle,
              isCrateCurrentTab
                ? {
                    paddingBottom: isDisputed ? 120 : 80,
                  }
                : null,
            ]}
            data={isCrateCurrentTab ? cratesData : sortUpdates()}
            renderItem={renderItem}
            keyboardShouldPersistTaps="handled"
            onRefresh={onRefresh}
            refreshing={isRefreshing}
          />
          {ordersData?.is_any_item_disputed && !isCrateCurrentTab ? (
            <TouchableOpacity
              testID="docl-see-all-samasya"
              style={styles.samasyaContainer}
              onPress={() => seeAllSamasya()}
            >
              <CustomText variant="title_16" style={{ color: "white" }}>
                {ordersData?.disputed_return_text}
              </CustomText>
              <View style={styles.arrowContainer}>
                <ChevronRight
                  pathFill="rgba(0, 0, 0, 0.4)"
                  style={styles.arrow}
                />
              </View>
            </TouchableOpacity>
          ) : null}
        </>
      )}

      {isCrateCurrentTab && (
        <BottomButton
          topComponent={
            isDisputed ? (
              <TouchableOpacity
                testID="docl-see-all-samasya"
                style={styles.samasyaContainer}
                onPress={() => seeAllSamasya()}
              >
                <CustomText variant="title_16" style={{ color: "white" }}>
                  {disputedtext}
                </CustomText>
                <View style={styles.arrowContainer}>
                  <ChevronRight
                    pathFill="rgba(0, 0, 0, 0.4)"
                    style={styles.arrow}
                  />
                </View>
              </TouchableOpacity>
            ) : null
          }
          onPress={() => {
            navigation.navigate("CrateScanner", {
              type: isEnabled ? "MY_ORDERS" : "",
              spoke: route?.params?.spokeName,
              date: route?.params?.date,
              rescheduleddata,
            });
          }}
          title={t(`${PATH}/scanCrate`)}
          secondaryBtn={is_cmo_cp && !cp_db_id}
          secondaryOnPress={() => {
            navigation.navigate("CrateScanner", {
              type: "ROUTE_INFO",
              spoke: route?.params?.spokeName,
              date: route?.params?.date,
            });
          }}
          secondaryTitle={t(`${PATH}/scanCrateForRoute`)}
        />
      )}
      <Modal
        visible={isLoading}
        transparent={true}
        statusBarTranslucent
        navigationBarTranslucent
      >
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </Modal>
    </View>
  );
}

export default SortingDeliveries;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerStyle: {
    paddingTop: 10,
  },

  tabContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: colors.white,
    alignItems: "center",
  },
  loadingView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  arrow: { height: 16, width: 16 },
  samasyaContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#4D4D4D",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  arrowContainer: {
    backgroundColor: "white",
    borderRadius: 12,
    height: 24,
    width: 24,
    alignItems: "center",
    justifyContent: "center",
  },
});
